// ============================================================================
// DENS CAKRA — KPI Intelligence Performance Management Model
// Deterministic, hierarchical KPI data derived from BINDA_LIST.
// Hierarki: Nasional → BINDA → Kabupaten/Kota → Unit → Koordinator → Personel
//
// Semua angka di-generate secara deterministik (di-seed dari indeks/kode) agar
// stabil antar render dan konsisten di setiap level drill-down. TIDAK memakai
// Math.random sehingga skor tidak berubah-ubah saat navigasi.
// ============================================================================

import { BINDA_LIST, Binda } from './binda'

export type Grade = 'A' | 'B' | 'C' | 'D'

export interface KpiComponents {
  produktivitas: number // bobot 25%
  kualitas: number // bobot 30%
  kecepatan: number // bobot 20%
  aktivitas: number // bobot 15% (Aktivitas Wilayah)
  kepatuhan: number // bobot 10%
}

export interface KpiBindaNode {
  kode: string
  binda: string
  provinsi: string
  score: number
  grade: Grade
  trend: number // % perubahan vs bulan lalu (boleh negatif)
  components: KpiComponents
  laporanValid: number
  laporanTotal: number
  rasioValid: number
  responsRataRata: string // mis. "14m"
  personelAktif: number
  rank: number // diisi setelah sorting
}

export interface KpiRegionNode {
  id: string
  nama: string // Kabupaten / Kota
  bindaKode: string
  score: number
  grade: Grade
  trend: number
  laporan: number
  rank: number
}

export interface KpiUnitNode {
  id: string
  nama: string
  bindaKode: string
  regionId: string
  score: number
  grade: Grade
  trend: number
  components: KpiComponents
  anggota: number
  rank: number
}

export interface KpiPersonNode {
  id: string
  nama: string
  unitId: string
  bindaKode: string
  pangkat: string
  score: number
  grade: Grade
  trend: number
  components: KpiComponents
  laporanValid: number
  responsTugas: string
  rankBinda: number
  rankNasional: number
  status: 'Aktif' | 'Perlu Pembinaan' | 'Tidak Aktif'
  foto: string
}

// --- Bobot Formula KPI ---------------------------------------------------
export const KPI_WEIGHTS = {
  produktivitas: 0.25,
  kualitas: 0.3,
  kecepatan: 0.2,
  aktivitas: 0.15,
  kepatuhan: 0.1,
}

export const KPI_FORMULA: {
  key: keyof KpiComponents
  label: string
  bobot: number
  komponen: string[]
}[] = [
  {
    key: 'produktivitas',
    label: 'Produktivitas',
    bobot: 25,
    komponen: ['Jumlah laporan', 'Jumlah aktivitas', 'Konsistensi pelaporan'],
  },
  {
    key: 'kualitas',
    label: 'Kualitas',
    bobot: 30,
    komponen: [
      'Laporan tervalidasi',
      'Laporan ditindaklanjuti',
      'Rasio laporan valid',
    ],
  },
  {
    key: 'kecepatan',
    label: 'Kecepatan',
    bobot: 20,
    komponen: [
      'Kecepatan pelaporan',
      'Kecepatan respon tugas',
      'Kecepatan tindak lanjut',
    ],
  },
  {
    key: 'aktivitas',
    label: 'Aktivitas Wilayah',
    bobot: 15,
    komponen: [
      'Cakupan wilayah',
      'Aktivitas lapangan',
      'Kehadiran operasional',
    ],
  },
  {
    key: 'kepatuhan',
    label: 'Kepatuhan',
    bobot: 10,
    komponen: ['Ketepatan administrasi', 'Kepatuhan SOP', 'Penyelesaian tugas'],
  },
]

// --- Helper deterministik ------------------------------------------------
function seedFromString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

// pseudo-random terdeterminasi 0..1 dari seed + salt
function rnd(seed: number, salt: number): number {
  const x = Math.sin(seed * 9301 + salt * 49297 + 233280) * 43758.5453
  return x - Math.floor(x)
}

export function gradeOf(score: number): Grade {
  if (score >= 90) return 'A'
  if (score >= 80) return 'B'
  if (score >= 70) return 'C'
  return 'D'
}

export const GRADE_META: Record<
  Grade,
  { label: string; color: string; desc: string }
> = {
  A: { label: 'A', color: 'var(--status-active)', desc: 'Sangat Baik' },
  B: { label: 'B', color: 'var(--status-info)', desc: 'Baik' },
  C: { label: 'C', color: 'var(--status-standby)', desc: 'Perlu Perhatian' },
  D: { label: 'D', color: 'var(--destructive)', desc: 'Perlu Pembinaan' },
}

// Warna heatmap berdasarkan band skor
export function scoreColor(score: number): string {
  if (score >= 90) return '#16a34a' // hijau tua — sangat baik
  if (score >= 80) return '#65a30d' // hijau — baik
  if (score >= 70) return '#eab308' // kuning — perlu perhatian
  return '#dc2626' // merah — perlu pembinaan
}

export function scoreBand(
  score: number,
): 'Sangat Baik' | 'Baik' | 'Perlu Perhatian' | 'Perlu Pembinaan' {
  if (score >= 90) return 'Sangat Baik'
  if (score >= 80) return 'Baik'
  if (score >= 70) return 'Perlu Perhatian'
  return 'Perlu Pembinaan'
}

function buildComponents(seed: number, baseScore: number): KpiComponents {
  const jitter = (salt: number) =>
    Math.round(
      Math.max(60, Math.min(100, baseScore + (rnd(seed, salt) - 0.5) * 14)),
    )
  return {
    produktivitas: jitter(1),
    kualitas: jitter(2),
    kecepatan: jitter(3),
    aktivitas: jitter(4),
    kepatuhan: jitter(5),
  }
}

export function weightedScore(c: KpiComponents): number {
  return Math.round(
    c.produktivitas * KPI_WEIGHTS.produktivitas +
      c.kualitas * KPI_WEIGHTS.kualitas +
      c.kecepatan * KPI_WEIGHTS.kecepatan +
      c.aktivitas * KPI_WEIGHTS.aktivitas +
      c.kepatuhan * KPI_WEIGHTS.kepatuhan,
  )
}

// ---------------------------------------------------------------------------
// LEVEL 2 — KPI per BINDA
// ---------------------------------------------------------------------------
function buildBindaNodes(): KpiBindaNode[] {
  const nodes = BINDA_LIST.map((b: Binda) => {
    const seed = seedFromString(b.kode)
    // skor dasar dari kpiWilayah, sedikit dinaikkan agar realistis untuk grade A nasional
    const base = Math.min(99, b.kpiWilayah + Math.round(rnd(seed, 9) * 4))
    const components = buildComponents(seed, base)
    const score = weightedScore(components)
    const trend = Math.round((rnd(seed, 7) - 0.45) * 30) // -13..+16
    const laporanTotal = b.laporanHariIni * 22 + Math.round(rnd(seed, 8) * 400)
    const rasioValid = 0.78 + rnd(seed, 11) * 0.2
    const laporanValid = Math.round(laporanTotal * rasioValid)
    const respons = 9 + Math.round(rnd(seed, 12) * 20)
    return {
      kode: b.kode,
      binda: b.binda,
      provinsi: b.provinsi,
      score,
      grade: gradeOf(score),
      trend,
      components,
      laporanValid,
      laporanTotal,
      rasioValid: Math.round(rasioValid * 100),
      responsRataRata: `${respons}m`,
      personelAktif: b.personelAktif,
      rank: 0,
    }
  })
  nodes.sort((a, b) => b.score - a.score)
  nodes.forEach((n, i) => (n.rank = i + 1))
  return nodes
}

export const KPI_BINDA: KpiBindaNode[] = buildBindaNodes()

export function getBinda(kode: string): KpiBindaNode | undefined {
  return KPI_BINDA.find((b) => b.kode === kode)
}

// ---------------------------------------------------------------------------
// LEVEL 1 — Agregat Nasional
// ---------------------------------------------------------------------------
const NATIONAL_SCORE = 89.4

export const KPI_NASIONAL = {
  score: NATIONAL_SCORE,
  grade: gradeOf(NATIONAL_SCORE),
  totalBinda: KPI_BINDA.length,
  totalPersonel: BINDA_LIST.reduce((s, b) => s + b.personelAktif, 0),
  trend: 6.2,
  components: {
    produktivitas: 88,
    kualitas: 91,
    kecepatan: 87,
    aktivitas: 90,
    kepatuhan: 92,
  } as KpiComponents,
}

export const TOP_10_BINDA = KPI_BINDA.slice(0, 10)
export const BOTTOM_10_BINDA = [...KPI_BINDA].slice(-10).reverse()

export const GRADE_DISTRIBUTION: { grade: Grade; jumlah: number }[] = (
  ['A', 'B', 'C', 'D'] as Grade[]
).map((g) => ({
  grade: g,
  jumlah: KPI_BINDA.filter((b) => b.grade === g).length,
}))

// Tren nasional multi-periode
function trendSeries(
  labels: string[],
  base: number,
  amp: number,
  rise: number,
): { label: string; score: number }[] {
  return labels.map((label, i) => ({
    label,
    score:
      Math.round(
        (base + (i / (labels.length - 1)) * rise + Math.sin(i) * amp) * 10,
      ) / 10,
  }))
}

export const TREN_NASIONAL = {
  harian: trendSeries(
    Array.from({ length: 7 }, (_, i) => `H-${7 - i}`),
    87,
    1.2,
    2.4,
  ),
  mingguan: trendSeries(['M-4', 'M-3', 'M-2', 'M-1', 'Ini'], 85, 1.5, 4.4),
  bulanan: trendSeries(
    ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun'],
    82,
    1.4,
    7.4,
  ),
  tahunan: trendSeries(['2022', '2023', '2024', '2025', '2026'], 78, 2, 11.4),
}

export const PERBANDINGAN_PERIODE = [
  { periode: 'Bulanan', sekarang: 89.4, sebelumnya: 84.1 },
  { periode: 'Triwulan', sekarang: 88.2, sebelumnya: 83.0 },
  { periode: 'Tahunan', sekarang: 86.7, sebelumnya: 79.5 },
]

// ---------------------------------------------------------------------------
// LEVEL 2.b — Kabupaten/Kota per BINDA
// ---------------------------------------------------------------------------
const KABUPATEN_SUFFIX = [
  'Kota Utama',
  'Kabupaten Barat',
  'Kabupaten Timur',
  'Kabupaten Selatan',
  'Kabupaten Utara',
  'Kota Pesisir',
]

const regionCache: Record<string, KpiRegionNode[]> = {}

export function getRegions(bindaKode: string): KpiRegionNode[] {
  if (regionCache[bindaKode]) return regionCache[bindaKode]
  const binda = getBinda(bindaKode)
  if (!binda) return []
  const seed = seedFromString(bindaKode)
  const count = 4 + Math.round(rnd(seed, 21) * 2) // 4..6
  const provShort = binda.provinsi.split(' ')[0]
  const nodes: KpiRegionNode[] = Array.from({ length: count }, (_, i) => {
    const s = seed + i * 13
    const score = Math.round(
      Math.max(68, Math.min(98, binda.score + (rnd(s, 31) - 0.5) * 18)),
    )
    return {
      id: `${bindaKode}-R${i + 1}`,
      nama: `${KABUPATEN_SUFFIX[i % KABUPATEN_SUFFIX.length]} ${provShort}`,
      bindaKode,
      score,
      grade: gradeOf(score),
      trend: Math.round((rnd(s, 32) - 0.45) * 28),
      laporan: 120 + Math.round(rnd(s, 33) * 480),
      rank: 0,
    }
  })
  nodes.sort((a, b) => b.score - a.score)
  nodes.forEach((n, i) => (n.rank = i + 1))
  regionCache[bindaKode] = nodes
  return nodes
}

// ---------------------------------------------------------------------------
// LEVEL 3 — Unit per BINDA
// ---------------------------------------------------------------------------
const UNIT_NAMA = [
  'Unit Analisis Strategis',
  'Unit Operasi Lapangan',
  'Unit Kontra Intelijen',
  'Unit Siber & Teknologi',
  'Unit Pengamanan Objek Vital',
  'Unit Deteksi Dini',
]

const unitCache: Record<string, KpiUnitNode[]> = {}

export function getUnits(bindaKode: string): KpiUnitNode[] {
  if (unitCache[bindaKode]) return unitCache[bindaKode]
  const binda = getBinda(bindaKode)
  if (!binda) return []
  const regions = getRegions(bindaKode)
  const seed = seedFromString(bindaKode + 'unit')
  const count = 4 + Math.round(rnd(seed, 41) * 2)
  const nodes: KpiUnitNode[] = Array.from({ length: count }, (_, i) => {
    const s = seed + i * 17
    const base = Math.max(
      66,
      Math.min(98, binda.score + (rnd(s, 51) - 0.5) * 20),
    )
    const components = buildComponents(s, base)
    const score = weightedScore(components)
    return {
      id: `${bindaKode}-U${i + 1}`,
      nama: UNIT_NAMA[i % UNIT_NAMA.length],
      bindaKode,
      regionId: regions[i % regions.length].id,
      score,
      grade: gradeOf(score),
      trend: Math.round((rnd(s, 52) - 0.45) * 26),
      components,
      anggota: 6 + Math.round(rnd(s, 53) * 18),
      rank: 0,
    }
  })
  nodes.sort((a, b) => b.score - a.score)
  nodes.forEach((n, i) => (n.rank = i + 1))
  unitCache[bindaKode] = nodes
  return nodes
}

export function getUnit(
  bindaKode: string,
  unitId: string,
): KpiUnitNode | undefined {
  return getUnits(bindaKode).find((u) => u.id === unitId)
}

// ---------------------------------------------------------------------------
// LEVEL 4 — Personel per Unit
// ---------------------------------------------------------------------------
const NAMA_DEPAN = [
  'Andi',
  'Budi',
  'Citra',
  'Dewi',
  'Eko',
  'Fajar',
  'Gita',
  'Hadi',
  'Indra',
  'Joko',
  'Kartika',
  'Lukman',
  'Maya',
  'Nanda',
  'Oki',
  'Putra',
  'Rama',
  'Sari',
  'Tono',
  'Wulan',
]
const NAMA_BELAKANG = [
  'Saputra',
  'Wijaya',
  'Pratama',
  'Santoso',
  'Hidayat',
  'Nugroho',
  'Permana',
  'Kusuma',
  'Maulana',
  'Anggara',
]
const PANGKAT = ['Letda', 'Lettu', 'Kapten', 'Mayor', 'Serka', 'Serma', 'Pelda']

const personCache: Record<string, KpiPersonNode[]> = {}

function buildPersonForUnit(unit: KpiUnitNode): KpiPersonNode[] {
  if (personCache[unit.id]) return personCache[unit.id]
  const seed = seedFromString(unit.id)
  const nodes: KpiPersonNode[] = Array.from(
    { length: unit.anggota },
    (_, i) => {
      const s = seed + i * 19
      const base = Math.max(
        58,
        Math.min(99, unit.score + (rnd(s, 61) - 0.45) * 26),
      )
      const components = buildComponents(s, base)
      const score = weightedScore(components)
      const nama = `${NAMA_DEPAN[Math.floor(rnd(s, 62) * NAMA_DEPAN.length)]} ${
        NAMA_BELAKANG[Math.floor(rnd(s, 63) * NAMA_BELAKANG.length)]
      }`
      const status: KpiPersonNode['status'] =
        score < 70
          ? 'Perlu Pembinaan'
          : rnd(s, 64) > 0.93
            ? 'Tidak Aktif'
            : 'Aktif'
      return {
        id: `${unit.id}-P${i + 1}`,
        nama,
        unitId: unit.id,
        bindaKode: unit.bindaKode,
        pangkat: PANGKAT[Math.floor(rnd(s, 65) * PANGKAT.length)],
        score,
        grade: gradeOf(score),
        trend: Math.round((rnd(s, 66) - 0.45) * 24),
        components,
        laporanValid: 8 + Math.round(rnd(s, 67) * 60),
        responsTugas: `${6 + Math.round(rnd(s, 68) * 22)}m`,
        rankBinda: 0,
        rankNasional: 0,
        status,
        foto: `https://i.pravatar.cc/150?u=${unit.id}-P${i + 1}`,
      }
    },
  )
  personCache[unit.id] = nodes
  return nodes
}

export function getPersonnelByUnit(
  bindaKode: string,
  unitId: string,
): KpiPersonNode[] {
  const unit = getUnit(bindaKode, unitId)
  if (!unit) return []
  return buildPersonForUnit(unit)
}

// Semua personel pada satu BINDA (dengan ranking BINDA terisi)
const bindaPersonCache: Record<string, KpiPersonNode[]> = {}
export function getPersonnelByBinda(bindaKode: string): KpiPersonNode[] {
  if (bindaPersonCache[bindaKode]) return bindaPersonCache[bindaKode]
  const units = getUnits(bindaKode)
  const all = units.flatMap((u) => buildPersonForUnit(u))
  all.sort((a, b) => b.score - a.score)
  all.forEach((p, i) => (p.rankBinda = i + 1))
  bindaPersonCache[bindaKode] = all
  return all
}

// Leaderboard nasional (subset BINDA teratas untuk performa)
let nationalPeopleCache: KpiPersonNode[] | null = null
export function getNationalLeaderboard(limit = 100): KpiPersonNode[] {
  if (!nationalPeopleCache) {
    const top = KPI_BINDA.slice(0, 12)
    const all = top.flatMap((b) => getPersonnelByBinda(b.kode))
    all.sort((a, b) => b.score - a.score)
    all.forEach((p, i) => (p.rankNasional = i + 1))
    nationalPeopleCache = all
  }
  return nationalPeopleCache.slice(0, limit)
}

export function getPerson(personId: string): KpiPersonNode | undefined {
  // personId format: <kode>-U<i>-P<j>
  const parts = personId.split('-')
  // bindaKode = first 2 segments e.g. BINDA-JK
  const bindaKode = `${parts[0]}-${parts[1]}`
  const all = getPersonnelByBinda(bindaKode)
  const found = all.find((p) => p.id === personId)
  if (found) {
    // pastikan ranking nasional terisi
    getNationalLeaderboard(99999)
  }
  return found
}

// Tren personel multi-periode (deterministik dari skor)
export function personTrend(
  person: KpiPersonNode,
): Record<'7h' | '30h' | '90h' | '1th', { label: string; score: number }[]> {
  const seed = seedFromString(person.id)
  const make = (n: number, prefix: string, amp: number) =>
    Array.from({ length: n }, (_, i) => ({
      label: `${prefix}${n - i}`,
      score:
        Math.round(
          Math.max(
            55,
            Math.min(
              100,
              person.score -
                person.trend +
                (person.trend * i) / (n - 1) +
                Math.sin(seed + i) * amp,
            ),
          ) * 10,
        ) / 10,
    }))
  return {
    '7h': make(7, 'H-', 1.4),
    '30h': make(10, 'H-', 1.8),
    '90h': make(12, 'M-', 2.2),
    '1th': make(12, 'B-', 2.6),
  }
}

// ---------------------------------------------------------------------------
// KPI ANALYTICS — insight otomatis & alert
// ---------------------------------------------------------------------------
export interface KpiInsight {
  id: string
  text: string
  tone: 'positif' | 'netral' | 'negatif'
}

export function getNationalInsights(): KpiInsight[] {
  const naik = [...KPI_BINDA]
    .filter((b) => b.trend > 0)
    .sort((a, b) => b.trend - a.trend)[0]
  const turun = [...KPI_BINDA].sort((a, b) => a.trend - b.trend)[0]
  const merah = KPI_BINDA.filter((b) => b.grade === 'D').length
  return [
    {
      id: 'ins-1',
      text: `KPI ${naik.binda} naik ${naik.trend}% dibanding bulan sebelumnya.`,
      tone: 'positif',
    },
    {
      id: 'ins-2',
      text: `Produktivitas wilayah meningkat namun validitas laporan ${turun.binda} menurun ${Math.abs(turun.trend)}%.`,
      tone: 'negatif',
    },
    {
      id: 'ins-3',
      text: `Personel dengan skor di bawah 70 meningkat 15% pada triwulan ini.`,
      tone: 'negatif',
    },
    {
      id: 'ins-4',
      text: `${merah} BINDA berada pada grade D dan memerlukan pembinaan prioritas.`,
      tone: 'negatif',
    },
    {
      id: 'ins-5',
      text: `Rata-rata kecepatan tindak lanjut nasional membaik menjadi 14,5 menit.`,
      tone: 'positif',
    },
  ]
}

export interface KpiAlert {
  id: string
  jenis: string
  target: string
  kode?: string
  detail: string
  severity: 'kritis' | 'peringatan' | 'info'
}

export function getKpiAlerts(): KpiAlert[] {
  const alerts: KpiAlert[] = []
  KPI_BINDA.forEach((b) => {
    if (b.trend <= -10) {
      alerts.push({
        id: `al-${b.kode}-drop`,
        jenis: 'KPI Turun >10%',
        target: b.binda,
        kode: b.kode,
        detail: `KPI ${b.binda} turun ${Math.abs(b.trend)}% bulan ini.`,
        severity: 'kritis',
      })
    }
    if (b.grade === 'D') {
      alerts.push({
        id: `al-${b.kode}-red`,
        jenis: 'Wilayah Merah',
        target: b.binda,
        kode: b.kode,
        detail: `${b.binda} berstatus merah (skor ${b.score}). Perlu pembinaan.`,
        severity: 'kritis',
      })
    }
    if (b.rasioValid < 82) {
      alerts.push({
        id: `al-${b.kode}-valid`,
        jenis: 'Validitas Laporan Turun',
        target: b.binda,
        kode: b.kode,
        detail: `Rasio laporan valid ${b.binda} turun ke ${b.rasioValid}%.`,
        severity: 'peringatan',
      })
    }
  })
  return alerts.slice(0, 12)
}

// ---------------------------------------------------------------------------
// HEATMAP — data per BINDA untuk peta/grid status performa
// ---------------------------------------------------------------------------
export interface HeatCell {
  kode: string
  binda: string
  provinsi: string
  score: number
  band: ReturnType<typeof scoreBand>
  color: string
  lat: number
  lng: number
}

export const KPI_HEATMAP: HeatCell[] = KPI_BINDA.map((b) => {
  const geo = BINDA_LIST.find((x) => x.kode === b.kode)!
  return {
    kode: b.kode,
    binda: b.binda,
    provinsi: b.provinsi,
    score: b.score,
    band: scoreBand(b.score),
    color: scoreColor(b.score),
    lat: geo.lat,
    lng: geo.lng,
  }
})

// ---------------------------------------------------------------------------
// FILTER — definisi field filter KPI lengkap (WAJIB)
// ---------------------------------------------------------------------------
export const KPI_FILTER_OPTIONS = {
  tahun: ['2026', '2025', '2024', '2023'],
  triwulan: ['Q1', 'Q2', 'Q3', 'Q4'],
  bulan: [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember',
  ],
  grade: ['A', 'B', 'C', 'D'],
  jenisLaporan: ['Rutin', 'Insidentil', 'Khusus', 'Mendesak'],
  kategori: [
    'Politik',
    'Sosial',
    'Ekonomi',
    'Keamanan',
    'Siber',
    'Terorisme',
    'Separatisme',
  ],
  statusVerifikasi: ['Terverifikasi', 'Menunggu', 'Ditolak'],
  statusTindakLanjut: ['Selesai', 'Proses', 'Belum Ditindaklanjut'],
}
